import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";

import { useSelector, useDispatch } from "react-redux";
import classNames from "classnames";
import {
	Modal,
	Button,
	ProgressIndicator
} from "rhombus-react";

import { CreativeVersions } from "reducers/CreativeVersions";
import { loadCreative } from "reducers/CreativeDetails";

import EmptySection from "widgets/EmptySection";
import Breadcrumb from "./Breadcrumb";

import AutoOptimizationPanel from "./AutoOptimizationPanel";
import SelectVersions from "./SelectVersions";

import { useModalStack, noop } from "utils/common";
import {
	TARGET_METHODS,
	LEGACY_TARGET_METHODS,
	MAX_VERSIONS_BY_TARGET_METHOD,
	WINNER_PERCENTAGE_MIN,
	WINNER_PERCENTAGE_MAX,
	WINNER_PERCENTAGE_DEFAULT,
	STATISTICAL_SIGNIFICANCE_MIN,
	STATISTICAL_SIGNIFICANCE_MAX,
	STATISTICAL_SIGNIFICANCE_DEFAULT,
	MIN_EVENTS_MIN,
	MIN_EVENTS_MAX,
	MIN_EVENTS_DEFAULT,
	TEMPERATURE_DEFAULT
} from "utils/constants";

import './MultiVariantModal.css';


const LEGACY_TARGET_METHODS_VALUES = Object.values(LEGACY_TARGET_METHODS);

export function isValidSelection({
	legacyTargetMethod,
	targetMethod,
	selectedObjective,
	winnerPercentage,
	statSigPercentage,
	minEvents,
	temperature,
}) {
	if(/modern/i.test(targetMethod)){
		return !!selectedObjective;
	}
	if(targetMethod === TARGET_METHODS.STATISTICAL_SIGNIFICANCE){
		const hasObjective = !!selectedObjective;
		const winnerValid = winnerPercentage >= WINNER_PERCENTAGE_MIN && winnerPercentage <= WINNER_PERCENTAGE_MAX;
		const statSigValid = statSigPercentage >= STATISTICAL_SIGNIFICANCE_MIN && statSigPercentage <= STATISTICAL_SIGNIFICANCE_MAX;
		const minEventsValid = minEvents >= MIN_EVENTS_MIN && minEvents <= MIN_EVENTS_MAX;

		return hasObjective && winnerValid && statSigValid && minEventsValid;
	}
	if(targetMethod === TARGET_METHODS.SOFTMAX){
		const hasObjective = !!selectedObjective;
		const hasTemperature = !!temperature;

		return hasObjective && hasTemperature;
	}

	if(targetMethod === TARGET_METHODS.LEGACY){
		return LEGACY_TARGET_METHODS_VALUES.includes(legacyTargetMethod);
	}

	return targetMethod === TARGET_METHODS.WEIGHTED_ROTATION;
}

export default function MultiVariantModal({
	libraryId,
	creativeId,
	edit,
	onClose,
}){
	const dispatch = useDispatch();
	let modalTitle = "Multi Variant Dynamic Creative";

	const commonModalProps = {
		title: modalTitle,
		type: "modal",
		className: "multi-variant-modal white-title-bar",
		xClose: true,
		allowFullScreen: false,
		bladeProps: {
			verticallyCenter: true,
		},
		onClose: closeModal,
		setAppTitle: false,
	};

	// eslint-disable-next-line no-unused-vars
	const [modalStack, { pushModal }, ModalStackHead] = useModalStack({ commonModalProps });
	const [targetMethod, setTargetMethod] = useState();

	const {
		isLoaded,
		creativeDecisioningName,
		isModernEnabled,
	} = useSelector((state) => {
		let loadedReqs = [
			state.libraryProperties?.isLoaded,
			state.advertiserDetails?.isLoaded
		];
		if(edit){
			loadedReqs.push(state.creativeDetails?.isLoaded);
		}
		return {
			isLoaded: loadedReqs.every(v => v),
			creativeDecisioningName: state.creativeDetails?.decisioningName,
			isModernEnabled: state?.advertiserDetails?.data?.isMtaOptimizationEnabled,
		};
	});

	let isRotationSelected = (targetMethod === TARGET_METHODS.WEIGHTED_ROTATION)
		, isAutoOptimizationSelected = (targetMethod && !isRotationSelected)
		, [selectedObjective, setSelectedObjective] = useState()
		, [legacyTargetMethod, setLegacyTargetMethod] = useState()
		, [winnerPercentage, setWinnerPercentage] = useState()
		, [statSigPercentage, setStatSigPercentage] = useState()
		, [minEvents, setMinEvents] = useState()
		, [temperature, setTemperature] = useState()
		;

	let canContinue = useMemo(() => isValidSelection({
		legacyTargetMethod, targetMethod, selectedObjective, winnerPercentage, statSigPercentage, minEvents, temperature,
	}), [
		legacyTargetMethod, targetMethod, selectedObjective, winnerPercentage, statSigPercentage, minEvents, temperature
	]);

	useEffect(() => {
		if(isLoaded && edit){
			setTargetMethod(creativeDecisioningName); /*
				This state update doesn't happen in time for `targetMethod` to be the correct value inside
				`openSelection`.

				We pass `openSelection` a `selectionMethod` argument, which corresponds to a `targetMethod` value,
				so we appropriately include weights, and dispatch the right creation action, but we have to set
				`targetMethod` so that the first step is populated correctly, if the user hits "Back" on the
				SelectVersions modal.
			*/
			openSelection(creativeDecisioningName);
		}
	}, [isLoaded, edit, creativeDecisioningName]);

	useEffect(() => {
		setSelectedObjective(undefined);
	}, [targetMethod]);

	useEffect(() => {
		if(targetMethod === TARGET_METHODS.SOFTMAX && !temperature){
			setTemperature(TEMPERATURE_DEFAULT);
		}
	}, [targetMethod, temperature]);

	useEffect(() => {
		if(targetMethod === TARGET_METHODS.STATISTICAL_SIGNIFICANCE){
			if(!winnerPercentage){
				setWinnerPercentage(WINNER_PERCENTAGE_DEFAULT);
			}
			if(!minEvents){
				setMinEvents(MIN_EVENTS_DEFAULT);
			}
			if(!statSigPercentage){
				setStatSigPercentage(STATISTICAL_SIGNIFICANCE_DEFAULT);
			}
		}
	}, [targetMethod, winnerPercentage, minEvents, statSigPercentage]);

	function closeModal(){
		onClose();
	}

	function openSelection(selectionMethod){
		let prompt = isRotationSelected ? "Select versions and weighting" : "Select versions to auto-optimize";
		function onSave({ success }){
			if(success){
				dispatch(CreativeVersions.loadCreativeVersions(libraryId, creativeId));
				dispatch(loadCreative(creativeId)); // We need this to update the DecisioningHeader
				closeModal();
			}
		}
		function Configured(props){
			let modalProps = {
				...props,
				libraryId,
				creativeId,
				prompt,
				includeWeights: (selectionMethod === TARGET_METHODS.WEIGHTED_ROTATION),
				maxSelectableVersions: MAX_VERSIONS_BY_TARGET_METHOD[selectionMethod],

				onSelect: (selected) => {
					/*
						`selected` is an array with objects in the form:
							{
								id,
								weightMultiplier // Only present if `includeWeights` is true
							}
					*/
					if(isAutoOptimizationSelected){
						return dispatch(CreativeVersions.createAutoOptimizedCreative(
							creativeId,
							selectionMethod,
							selected,
							legacyTargetMethod,
							selectedObjective,
							winnerPercentage,
							statSigPercentage,
							minEvents,
							temperature
						))
							.then(onSave);
					}
					return dispatch(CreativeVersions.createWeightedRotationCreative(libraryId, creativeId, selected))
						.then(onSave);
				},
			};

			return <SelectVersions {...modalProps} />;
		}
		pushModal(Configured);
	}

	function Body() {
		let autoProps = {
			className:classNames("auto-optimization", {
				selected: isAutoOptimizationSelected,
				"can-continue":canContinue,
			}),
		};
		if(!isAutoOptimizationSelected){
			/**
			 * This onClick is consuming the onClick of the sub radio buttons when "Legacy" is selected
			 * There's probably a better way to do this
			 * 	- ideally using redux-form for all these fields instead of setState()
			 * But in the interest of time, only adding onClick when necessary works
			 */
			autoProps.onClick = () => {
				if(isModernEnabled){
					setTargetMethod(TARGET_METHODS.MODERN);
				} else {
					setTargetMethod(TARGET_METHODS.LEGACY);
					setLegacyTargetMethod(LEGACY_TARGET_METHODS.CLICK_THROUGH);
				}
			};
		}
		return (
			<div className="body-container">
				<ProgressIndicator key="loading" isLoading={!isLoaded} />
				<Breadcrumb currentStep={0} />
				<header className="header-info">
					<strong>Select how you would like to serve the versions for this creative</strong>
				</header>
				<div className="selection-routes">
					<div
						className={classNames("rotation", {
							selected: isRotationSelected,
							"can-continue":canContinue,
						})}
						onClick = {() => { setTargetMethod(TARGET_METHODS.WEIGHTED_ROTATION); }}
					>
						<EmptySection icon="rotation" header="Weighted Rotation" />
					</div>

					<div {...autoProps}>
						<AutoOptimizationPanel
							isSelected = {isAutoOptimizationSelected}
							isModernEnabled={isModernEnabled}
							currentValue = {targetMethod}
							onChange = {setTargetMethod}
							legacyTargetMethod = {legacyTargetMethod}
							onLegacyChange = {setLegacyTargetMethod}
							onObjective={setSelectedObjective}
							selectedObjective={selectedObjective}
							winnerPercentage = {winnerPercentage}
							onWinnerPercentageChange = {setWinnerPercentage}
							statSigPercentage = {statSigPercentage}
							onStatSigPercentageChange = {setStatSigPercentage}
							minEvents = {minEvents}
							onMinEventsChange = {setMinEvents}
							temperature = {temperature}
							onTemperatureChange = {setTemperature}
						/>
					</div>
				</div>
			</div>
		);
	}

	function Footer() {
		return (
			<>
				<Button
					theme="default"
					type="reset"
					onClick = { closeModal }
				>
					Cancel
				</Button>

				<Button
					onClick = { () => { openSelection(targetMethod); } }
					disabled = { !canContinue }
				>
					Next to Select Versions
				</Button>
			</>
		);
	}

	if(ModalStackHead){
		return <ModalStackHead />;
	}

	return (
		<Modal
			{...commonModalProps}
			renderContent = { Body }
			renderFooter = { Footer }
		/>
	);
}

MultiVariantModal.displayName = "MultiVariantModal";
MultiVariantModal.propTypes = {
	libraryId: PropTypes.string.isRequired,
	creativeId: PropTypes.string.isRequired,
	onClose: PropTypes.func,
	edit: PropTypes.bool,
};
MultiVariantModal.defaultProps = {
	onClose: noop,
	edit: false,
};
